'use client'
import classNames from 'classnames'
import type { DebouncedFuncLeading } from 'lodash'
import throttle from 'lodash/throttle'
import Link from 'next/link'
import type { FC } from 'react'
import React from 'react'
import { useEffect, useState } from 'react'
import { AddressPrompt } from '../address-prompt/AddressPrompt'
import { LocationScrollDrawer } from '../location-scroll-drawer/LocationScrollDrawer'
import { UserButton } from '../user-button/UserButton'
import styles from './HeaderDesktop.module.scss'
import { useDeliveryAddress } from '@/delivery-address/client'
import { useLocalisation } from '@/tools/i18n/client'
import { useIntersectionDetection } from '@/tools/intersection-detection/use-intersection-detection'
import { useErrorHandling } from '@/tools/o11y/client'
import { LogoSize, LogoSurface, LogoVariant, MainLogo } from '@/tools/ui'
import type { HeaderColorType } from '@/tools/ui/components/header/types'
import { UserSearchInput } from '@/tools/ui/components/user-search-input/UserSearchInput'

interface Props {
  color?: HeaderColorType
  className?: string
  hasScrollLocationDrawer?: boolean
  shouldShowAddress?: boolean
  canShowSearch?: boolean
  shouldShowUserButton?: boolean
  children?: React.ReactNode
  forceAddressPicker?: boolean
}

export const HeaderDesktop: FC<Props> = ({
  className,
  shouldShowAddress,
  canShowSearch,
  hasScrollLocationDrawer,
  color,
  shouldShowUserButton,
  children,
  forceAddressPicker,
}) => {
  const { getLanguage } = useLocalisation()
  const language = getLanguage()
  const { deliveryAddress } = useDeliveryAddress()
  const { execWithErrorTracking, withErrorTracking } = useErrorHandling()
  const isBrandHeader = color === 'brand'
  const logoSurface = isBrandHeader ? LogoSurface.onBrand : LogoSurface.onPrimary
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    let handleScroll: DebouncedFuncLeading<() => void> | undefined

    execWithErrorTracking(() => {
      handleScroll = throttle(() => {
        const offset = window.scrollY
        setScrolled(offset > 0)
      }, 200)

      window.addEventListener('scroll', handleScroll)
    })

    return withErrorTracking(() => {
      if (handleScroll) {
        handleScroll.cancel()
        window.removeEventListener('scroll', handleScroll)
      }
    })
  }, [execWithErrorTracking, withErrorTracking])

  const { isIntersecting, isAbove } = useIntersectionDetection(
    'hero-container-input',
    'header-desktop'
  )
  const isIntersectingOrAbove = isIntersecting || isAbove

  const showLocationScrollDrawer = Boolean(
    hasScrollLocationDrawer && !deliveryAddress && isIntersectingOrAbove
  )

  return (
    <div
      className={classNames(styles.headerDesktop, className, {
        [styles.scrolled]: scrolled && !isBrandHeader,
      })}
      id={'header-desktop'}
    >
      <section className={styles.topContainer}>
        <div className={styles.leftSection}>
          <Link className={styles.logoLink} href={`/${language}`} prefetch={false}>
            <MainLogo
              className={styles.logoImage}
              size={LogoSize.medium}
              surface={logoSurface}
              variant={LogoVariant.logotype}
            />
          </Link>
        </div>
        <div className={styles.centerSection}>
          {shouldShowAddress && (
            <AddressPrompt
              color={color}
              forceAddressPicker={forceAddressPicker}
              isIntersecting={isIntersectingOrAbove}
              showLocationScrollDrawer={showLocationScrollDrawer}
            />
          )}
          {canShowSearch && (
            <div className={styles.searchInput}>
              <UserSearchInput />
            </div>
          )}
          {children}
        </div>
        {shouldShowUserButton && (
          <div className={styles.rightSection}>
            <UserButton color={color} isIntersecting={isIntersectingOrAbove} />
          </div>
        )}
      </section>
      <LocationScrollDrawer isShown={showLocationScrollDrawer} />
    </div>
  )
}
